import { Maximize2 } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button, DataTable, type DataTableColumns } from '@/components/ui'
import type { Check } from '@/modules/projects'
import { chatChecksColumns, useCheckTransactions } from '@/modules/projects'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'

export interface ChecksSpec {
  // Wire field is `check_ids`; camelcaseSpec already converted.
  checkIds: string[]
  title?: string | null
}

interface ChecksArtifactProps {
  spec: ChecksSpec
}

// The chat-side checks artifact. The model emits a list of check ids
// (typically from a prior query_checks call); we hydrate the rows from
// the already-loaded checks slice of the project store and render them
// with the same lightweight table the transactions artifact uses.
export default function ChecksArtifact({ spec }: ChecksArtifactProps) {
  const allChecks = useCheckTransactions()
  const [expanded, setExpanded] = useState(false)

  const { rows, missingCount } = useMemo(
    () => resolveRows(allChecks, spec.checkIds),
    [allChecks, spec.checkIds],
  )

  const columns = useMemo(() => chatChecksColumns() as DataTableColumns, [])

  const isEmpty = rows.length === 0
  const title = spec.title?.trim() || 'Checks'

  return (
    <>
      <figure className="my-1 w-full overflow-hidden rounded-lg border bg-card">
        <figcaption className="flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm font-medium text-foreground">
          <span className="min-w-0 flex-1 truncate">
            {title}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {countLabel(rows.length, missingCount)}
            </span>
          </span>
          {!isEmpty && (
            <Button
              type="button"
              theme="ghost"
              size="xs"
              isIcon
              onClick={() => setExpanded(true)}
              title="Expand"
              aria-label="Expand"
            >
              <Maximize2 className="size-3.5" aria-hidden />
            </Button>
          )}
        </figcaption>

        {isEmpty ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            None of the named checks are in this project.
          </div>
        ) : expanded ? (
          <InlineStubWhileExpanded />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            height="h-80"
            enableRowSelection={false}
          />
        )}
      </figure>

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={title}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          <DataTable
            columns={columns}
            data={rows}
            height="flex-1"
            enableRowSelection={false}
          />
        </div>
      </ExpandedArtifactDialog>
    </>
  )
}

function InlineStubWhileExpanded() {
  return (
    <div className="flex h-80 items-center justify-center px-4 text-sm text-muted-foreground">
      Showing in the expanded view — close to view here.
    </div>
  )
}

// Looks ids up in the loaded checks slice, preserving the order the
// model named. Tracks how many ids didn't resolve so we can footnote
// the header when the project's checks haven't fully loaded yet (or the
// model named an id that isn't there).
function resolveRows(
  checks: Check[],
  ids: string[] | undefined,
): { rows: Check[]; missingCount: number } {
  if (!ids || ids.length === 0) {
    return { rows: [], missingCount: 0 }
  }
  const byId = new Map<string, Check>()
  for (const check of checks) {
    byId.set(String(check.id), check)
  }
  const rows: Check[] = []
  let missingCount = 0
  for (const id of ids) {
    const match = byId.get(String(id))
    if (match) {
      rows.push(match)
    } else {
      missingCount += 1
    }
  }
  return { rows, missingCount }
}

function countLabel(found: number, missing: number): string {
  if (missing === 0)
    return `${found.toLocaleString()} ${plural('check', found)}`
  return `${found.toLocaleString()} of ${(found + missing).toLocaleString()} (${missing} not loaded)`
}

function plural(noun: string, count: number): string {
  return count === 1 ? noun : `${noun}s`
}
